const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = async (endpoint, method = 'GET', body = null, extraHeaders = null) => {
  const options = {
    method,
    headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
  };
  if (body) options.body = JSON.stringify(body);
  const res = await fetch(`${BASE_URL}${endpoint}`, options);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  return res.json();
};

// requireSession-gated calls carry the real server-issued session token (see server.staging.js's
// requireSession) as a Bearer token, never a client-supplied id — sessionToken comes from the
// login response and rides along on the `user` object wherever it's already passed down.
const apiAuth = (sessionToken, endpoint, method = 'GET', body = null) =>
  api(endpoint, method, body, { Authorization: `Bearer ${sessionToken}` });

// ── Players ──────────────────────────────────────────────────────────────────
export const getPlayers    = ()         => api('/api/players');
export const createPlayer  = (data)     => api('/api/players', 'POST', data);
export const updatePlayer  = (id, data) => api(`/api/players/${id}`, 'PUT', data);

// ── Wellness ─────────────────────────────────────────────────────────────────
export const getWellness   = ()     => api('/api/wellness');
export const saveWellness  = (data) => api('/api/wellness', 'POST', data);

// ── Oura Ring connect (Phase 1) ────────────────────────────────────────────────
export const startOuraConnect = (sessionToken) => apiAuth(sessionToken, '/api/oura/connect/start', 'POST');
export const getOuraStatus    = (sessionToken) => apiAuth(sessionToken, '/api/oura/status');

// ── Oura Ring staff summary (Phase 4) ──────────────────────────────────────────
export const getOuraSummary     = () => api('/api/oura/summary');
export const getOuraConnections = () => api('/api/oura/connections');

// ── RPE ──────────────────────────────────────────────────────────────────────
export const getRPE        = ()     => api('/api/rpe');
export const saveRPE       = (data) => api('/api/rpe', 'POST', data);
export const getRPEAnalysis = (dateFrom, dateTo) =>
  api(`/api/rpe/analysis${dateFrom && dateTo ? `?date_from=${dateFrom}&date_to=${dateTo}` : ''}`);

// ── Missing Submissions popup — manual per-player/day exclusions ─────────────────
export const getWellnessExclusions    = ()                              => api('/api/wellness-exclusions');
export const addWellnessExclusion     = (playerId, date, form, reason)  => api('/api/wellness-exclusions', 'POST', { player_id: playerId, date, form, reason });
export const removeWellnessExclusion  = (id)                            => api(`/api/wellness-exclusions/${id}`, 'DELETE');

// ── GPS ──────────────────────────────────────────────────────────────────────
export const getGPS           = ()                       => api('/api/gps');
export const saveGPS          = (data)                   => api('/api/gps', 'POST', data);
export const deleteGPS        = (id)                     => api(`/api/gps/${id}`, 'DELETE');
export const patchGPSMaxSpeed = (sessionId, playerId, v) => api(`/api/gps/${sessionId}`, 'PATCH', { player_id: playerId, max_speed: v });
export const updateSessionNotes = (sessionId, notes) => api(`/api/gps/${sessionId}/notes`, 'PUT', { notes });
export const getGpsSessionPlayers = (sessionId) => api(`/api/gps/sessions/${sessionId}/players`);
export const previewGpsEstimate = (sessionId, playerId, missedMins) =>
  api(`/api/gps/sessions/${sessionId}/estimate`, 'POST', { player_id: playerId, missed_mins: missedMins });
export const commitGpsEstimate = (sessionId, playerId, missedMins, userId, requesterRole, metrics) =>
  api(`/api/gps/sessions/${sessionId}/commit-estimate`, 'POST', { player_id: playerId, missed_mins: missedMins, user_id: userId, requesterRole, metrics });
export const commitGpsManualValues = (sessionId, playerId, userId, requesterRole, values) =>
  api(`/api/gps/sessions/${sessionId}/commit-manual`, 'POST', { player_id: playerId, user_id: userId, requesterRole, values });
export const revertGpsEstimate = (sessionId, playerId, userId, requesterRole) =>
  api(`/api/gps/sessions/${sessionId}/revert-estimate`, 'POST', { player_id: playerId, user_id: userId, requesterRole });

// ── Scenarios ─────────────────────────────────────────────────────────────────
export const getScenarios    = ()     => api('/api/scenarios');
export const createScenario  = (data) => api('/api/scenarios', 'POST', data);
export const updateScenario  = (id, data) => api(`/api/scenarios/${id}`, 'PUT', data);
export const deleteScenario  = (id)   => api(`/api/scenarios/${id}`, 'DELETE');

// ── Player position history (date-scoped) ──────────────────────────────────────
export const getPlayerPositions    = ()     => api('/api/player-positions');
export const createPlayerPosition  = (data) => api('/api/player-positions', 'POST', data);
export const deletePlayerPosition  = (id)   => api(`/api/player-positions/${id}`, 'DELETE');

// ── Trainer ratings (Tactical/Identity) ─────────────────────────────────────────
export const getKpiDefinitions      = (pillar) => api(`/api/kpi-definitions${pillar ? `?pillar=${pillar}` : ''}`);
export const getTrainerRatings      = (params) => api(`/api/trainer-ratings?${new URLSearchParams(params)}`);
export const saveMatchRatings       = (data)   => api('/api/trainer-ratings/match', 'POST', data);
export const saveTrainingRatings    = (data)   => api('/api/trainer-ratings/training', 'POST', data);
export const deleteTrainerRatings   = (params) => api(`/api/trainer-ratings?${new URLSearchParams(params)}`, 'DELETE');

// ── 360-view pillar scores (Stage 3 rollup) ─────────────────────────────────────
export const getPlayerPillarScores = (playerId, period) => api(`/api/players/${playerId}/pillar-scores?period=${period}`);
export const getPlayerPillarTrend  = (playerId, period) => api(`/api/players/${playerId}/pillar-scores-trend?period=${period}`);
export const getSquadPillarScores  = (period) => api(`/api/players/pillar-scores?period=${period}`);
export const getPhysicalBreakdown  = (playerId, period) => api(`/api/players/${playerId}/physical-breakdown?period=${period}`);
export const getPillarKpiBreakdown = (pillar, period) => api(`/api/players/pillar-kpi-breakdown?pillar=${pillar}&period=${period}`);
// matchIds (optional, array of match_id): when given, overrides `period` entirely — shows one
// specific game's own numbers, or the pooled average across several selected games.
export const getTechnicalKpiBreakdown = (period, matchIds) => {
  const q = (matchIds && matchIds.length) ? `match_ids=${matchIds.join(',')}` : `period=${period}`;
  return api(`/api/players/technical-kpi-breakdown?${q}`);
};
export const getPillarWeights      = ()       => api('/api/pillar-weights');
export const getFormations         = ()       => api('/api/formations');
// period ('season' | 'preseason', optional): overrides `limit` entirely — every match in that
// window instead of just the last N.
export const getPlayerTechnicalTrend = (playerId, { limit = 8, period } = {}) =>
  api(`/api/players/${playerId}/technical-trend?${period ? `period=${period}` : `limit=${limit}`}`);

// ── Individual Progress report (2026-08-05) — historical per-scenario-week scores, distinct
// from the week/28day/season periods above (always anchored to real today). "Weeks" are team
// scenario instances, listed by getIndividualProgressWeeks and referenced by scenarioId.
export const getIndividualProgressWeeks = () => api('/api/individual-progress/weeks');
export const getIndividualProgress = (playerIds, scenarioIds) =>
  api(`/api/individual-progress?player_ids=${playerIds.join(',')}&scenario_ids=${scenarioIds.join(',')}`);

// ── Stage 7c: lineup save/load/default ──────────────────────────────────────────
export const getLineups          = ()               => api('/api/lineups');
export const getDefaultLineup    = ()               => api('/api/lineups/default');
export const getLineup           = (id)              => api(`/api/lineups/${id}`);
export const createLineup        = (data)            => api('/api/lineups', 'POST', data);
export const updateLineup        = (id, data)        => api(`/api/lineups/${id}`, 'PUT', data);
export const setDefaultLineup    = (id)              => api(`/api/lineups/${id}/set-default`, 'POST');

// ── Official Line-up (2026-08-07) ────────────────────────────────────────────
export const getMatchesForLineup   = ()                    => api('/api/matches/for-lineup');
export const getOfficialLineup     = (matchDate, opponentName) =>
  api(`/api/lineups/official?${new URLSearchParams({ matchDate, opponentName })}`);
export const saveOfficialLineup    = (data)                 => api('/api/lineups/official', 'POST', data);
export const getOfficialLineupStats = ()                    => api('/api/lineups/official/stats');

// ── Auth ──────────────────────────────────────────────────────────────────────
export const loginUser = (username, password) =>
  api('/api/auth/login', 'POST', { username, password });

// ── Einstein (Home console, Phase 1) ────────────────────────────────────────────
export const queryEinstein = (sessionToken, question, priorContext, conversationId) =>
  apiAuth(sessionToken, '/api/einstein/query', 'POST', { question, priorContext, conversationId });
export const getEinsteinSuggestions = (sessionToken) =>
  apiAuth(sessionToken, '/api/einstein/suggestions');
export const listEinsteinConversations = (sessionToken) =>
  apiAuth(sessionToken, '/api/einstein/conversations');
export const getEinsteinConversation = (sessionToken, id) =>
  apiAuth(sessionToken, `/api/einstein/conversations/${id}`);
export const deleteEinsteinConversation = (sessionToken, id) =>
  apiAuth(sessionToken, `/api/einstein/conversations/${id}`, 'DELETE');

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers   = ()         => api('/api/users');
export const createUser = (data)     => api('/api/users', 'POST', data);
export const updateUser = (id, data) => api(`/api/users/${id}`, 'PUT', data);
export const deleteUser        = (id)                           => api(`/api/users/${id}`, 'DELETE');
export const changePassword    = (userId, currentPassword, newPassword) =>
  api('/api/auth/change-password', 'PUT', { userId, currentPassword, newPassword });
export const getUserPassword   = (id)                           => api(`/api/users/${id}/password`);

// ── Usage tracking (2026-08-23) — admin-only overview of who's using the app, when, and how much.
// trackUsage swallows its own errors: it's fire-and-forget instrumentation on every navigation,
// and a failed log call should never surface as a broken page to the user.
export const trackUsage = (userId, username, name, page) =>
  api('/api/usage/track', 'POST', { userId, username, name, page }).catch(() => {});
export const getUsageSummary = (requesterId) => api(`/api/usage/summary?requesterId=${requesterId}`);

// ── Opponents ─────────────────────────────────────────────────────────────────
export const getOpponents    = ()         => api('/api/opponents');
export const createOpponent  = (data)     => api('/api/opponents', 'POST', data);
export const updateOpponent  = (id, data) => api(`/api/opponents/${id}`, 'PUT', data);
export const seedOpponents   = ()         => api('/api/opponents/seed', 'POST');

// ── Goalkeeper sessions ───────────────────────────────────────────────────────
export const getGkSessions    = ()     => api('/api/gk-sessions');
export const uploadGkSessions = (rows) => api('/api/gk-sessions/upload', 'POST', rows);
export const deleteGkSession  = (sessionDate, sessionTitle) => {
  const q = new URLSearchParams({ session_date: sessionDate, session_title: sessionTitle });
  return api(`/api/gk-sessions/session?${q}`, 'DELETE');
};

// ── Technical stats (SciSports CSV import) ──────────────────────────────────────
// Bypasses the shared api() helper — its generic "API error: 400" would swallow this
// endpoint's specific match-resolution error message, but a clear error (not a silent skip)
// is the whole point of that check (see technicalImport.js). Doesn't touch api() itself,
// which every other caller in the app relies on behaving as-is.
export const uploadTechnicalStats = async (rows) => {
  const res = await fetch(`${BASE_URL}/api/technical-stats/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rows),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `API error: ${res.status}`);
  return body;
};
export const getTechnicalStats    = ()          => api('/api/technical-stats');
export const deleteTechnicalStats = (matchId)   => api(`/api/technical-stats/${matchId}`, 'DELETE');

// ── Technical stats (KNVB CSV import, 2026-08-17) ────────────────────────────────
// Second source alongside SciSports above — separate endpoint/function (not a format param
// on the existing one) since the two CSVs have entirely different column sets and the backend
// importer is a separate function (ingestKnvbCsv) reading them directly, same reasoning as the
// SciSports upload bypassing api() for its own specific error message.
export const uploadTechnicalStatsKnvb = async (rows) => {
  const res = await fetch(`${BASE_URL}/api/technical-stats/upload-knvb`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rows),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `API error: ${res.status}`);
  return body;
};

// ── Tracking (Team Comparison — KNVB/SciSports opponent physical CSV) ───────────
// Bypasses api(), same reason as the two calls above: a 409 (match already imported) carries a
// summary of the existing record in its body, which the shared helper's generic "API error: 409"
// would throw away — the Upload Zone needs that summary to show the overwrite confirmation.
export const uploadTrackingImport = async (rows, filename, overwrite = false) => {
  const res = await fetch(`${BASE_URL}/api/tracking/import${overwrite ? '?overwrite=true' : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, filename }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.error || `API error: ${res.status}`);
    err.status = res.status;
    err.existing = body?.existing || null;
    throw err;
  }
  return body;
};
export const getTrackingMatches   = ()   => api('/api/tracking/matches');
export const deleteTrackingMatch  = (id) => api(`/api/tracking/matches/${id}`, 'DELETE');
export const getTrackingComparison        = (id, basis = 'heralabx')       => api(`/api/tracking/comparison/${id}?basis=${basis}`);
export const getTrackingComparisonByMatch = (heraclesMatchId, basis = 'heralabx') => api(`/api/tracking/comparison/by-match/${heraclesMatchId}?basis=${basis}`);
export const patchTrackingCalibration     = (id, excluded, reason) => api(`/api/tracking/matches/${id}/calibration`, 'PATCH', { excluded, reason });

// Period Comparison (2026-09-28). getTrackingPeriodsByMatch resolves a GPS match session to its
// tracking match + stored periods (404 when no tracking file). comparePeriods keeps the server's
// error text (400 invalid blocks, 409 no period rows) — the generic api() helper drops it.
export const getTrackingPeriodsByMatch = (heraclesMatchId) => api(`/api/tracking/periods/by-match/${heraclesMatchId}`);
export const comparePeriods = async (body) => {
  const res = await fetch(`${BASE_URL}/api/tracking/periods/compare`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(json?.error || `API error: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json;
};

// ── CMJ (ForceDecks CSV import) ──────────────────────────────────────────────────
// Writes into the same test_cmj table/columns the manual Physical Test Input CMJ form uses —
// see server.production.js's /api/cmj/upload for the upsert.
export const uploadCmjTests = (rows) => api('/api/cmj/upload', 'POST', rows);

// ── Drills ─────────────────────────────────────────────────────────────────────
// sumExisting mirrors saveGPS's sum_existing — see server.production.js's /api/drills/batch:
// false (default) replaces the session's drills, true appends (the confirmed "two genuine
// sessions share this label" edge case).
export const saveDrillBatch  = (drills, { sumExisting = false } = {}) =>
  api('/api/drills/batch', 'POST', { drills, sum_existing: sumExisting });
// Either a sessionId (existing per-session callers) or { from, to } for a date-range fetch
// (Metrics Table page) — kept as one function since every caller wants "the drills matching
// this filter," just with a different filter shape.
export const getDrills = (arg) => {
  if (arg && typeof arg === 'object') {
    const q = new URLSearchParams({ from: arg.from, to: arg.to });
    return api(`/api/drills?${q}`);
  }
  return api(`/api/drills?session_id=${arg}`);
};

// ── GPS custom metrics ────────────────────────────────────────────────────────
export const getGpsCustomMetrics    = ()     => api('/api/gps/custom-metrics');
export const createGpsCustomMetric  = data   => api('/api/gps/custom-metrics', 'POST', data);
export const deleteGpsCustomMetric  = id     => api(`/api/gps/custom-metrics/${id}`, 'DELETE');

// ── MSS (Maximal Sprinting Speed) ────────────────────────────────────────────
// The screened, bounded-window replacement for "highest value ever recorded".
// Every MSS number renders with its confidence badge and observation count — a bare
// number with no confidence indicator is a bug. See docs/mss-methodology.md.
export const getMssSquad        = ()            => api('/api/mss/squad');
export const getMssPlayer       = (playerId)    => api(`/api/mss/${playerId}`);
export const getMssHistory      = (playerId)    => api(`/api/mss/${playerId}/history`);
export const getMssReviewQueue  = (includeDecided = false) =>
  api(`/api/mss/review${includeDecided ? '?include_decided=true' : ''}`);
export const decideMssReview    = (id, data)    => api(`/api/mss/review/${id}`, 'POST', data);
export const getMssDrillClasses = ()            => api('/api/mss/drill-classes');
export const setMssDrillClass   = (data)        => api('/api/mss/drill-classes', 'PUT', data);
export const getMssConfig       = ()            => api('/api/mss/config');
export const saveMssConfig      = (config)      => api('/api/mss/config', 'PUT', { config });
export const recomputeMss       = (dryRun)      => api('/api/mss/recompute', 'POST', { dry_run: !!dryRun });
export const getMatchDrills       = ()                           => api('/api/drills/match');
export const getPlayerMatchDrills = (playerName, sessionType) => {
  const q = new URLSearchParams({ player_name: playerName, ...(sessionType ? { session_type: sessionType } : {}) });
  return api(`/api/drills/player?${q}`);
};
export const getPlayerMatchSessions = (playerName) => {
  const q = new URLSearchParams({ player_name: playerName });
  return api(`/api/gps/player-matches?${q}`);
};

// ── Comparison Builder ────────────────────────────────────────────────────────
export const getMatchAvailableDrills = matchId => api(`/api/matches/${matchId}/drills`);
export const getMatchDrillMetrics = (matchId, { scope, playerId, metrics }) => {
  const q = new URLSearchParams({ scope, metrics: metrics.join(',') });
  if (scope === 'player' && playerId) q.set('player_id', playerId);
  return api(`/api/matches/${matchId}/drill-metrics?${q}`);
};
export const getComparisonPresets    = ()     => api('/api/comparison-presets');
export const createComparisonPreset  = data   => api('/api/comparison-presets', 'POST', data);
export const deleteComparisonPreset  = id     => api(`/api/comparison-presets/${id}`, 'DELETE');

// ── Load Scenario Templates ────────────────────────────────────────────────────
export const getLoadScenarios    = ()         => api('/api/load-scenarios');
export const createLoadScenario  = (data)     => api('/api/load-scenarios', 'POST', data);
export const updateLoadScenario  = (id, data) => api(`/api/load-scenarios/${id}`, 'PUT', data);
export const deleteLoadScenario  = (id)       => api(`/api/load-scenarios/${id}`, 'DELETE');

// ── Target resolution (2026-09-01) — see targetResolution.js (backend) for the single source of
// truth every training-load page now goes through instead of computing targets independently.
export const getRawTargets = () => api('/api/targets/raw');
export const resolveTargetsBatch = (items) => api('/api/targets/resolve-batch', 'POST', { items });

export const syncStaging = () => api('/api/admin/sync-staging', 'POST');

// ── GPS & Load Week Summary (staging only) ──────────────────────────────────────
export const getGpsWeekSummaryScenarios = () => api('/api/gps/week-summary/scenarios');
export const getGpsWeekSummary = (scenarioId) => api(`/api/gps/week-summary${scenarioId ? `?scenarioId=${encodeURIComponent(scenarioId)}` : ''}`);

// ── GPS & Load Trends — requireSession-gated (read: any logged-in user; override: admin) ──
export const getGpsTrends = (sessionToken, asOf) =>
  apiAuth(sessionToken, `/api/gps/trends${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`);
export const setTrendRoleOverride = (sessionToken, playerId, role) =>
  apiAuth(sessionToken, `/api/gps/trends/role-override/${playerId}`, 'PUT', { role });
export const clearTrendRoleOverride = (sessionToken, playerId) =>
  apiAuth(sessionToken, `/api/gps/trends/role-override/${playerId}`, 'DELETE');

// ── Club Planner (staging only) ─────────────────────────────────────────────
// Session-authenticated like apiAuth, but keeps the server's own error message ("Planner edit
// access required", "Feed fixtures cannot be deleted", ...) so the page can show it.
const plannerApi = async (sessionToken, endpoint, method = 'GET', body = null) => {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.error || `API error: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
};
export const getPlannerSeason          = (t, from, to)      => plannerApi(t, `/api/planner/season?from=${from}&to=${to}`);
export const getPlannerFixtureChanges  = (t)                => plannerApi(t, '/api/planner/fixture-changes');
export const acceptPlannerFixtureChange = (t, id)           => plannerApi(t, `/api/planner/fixture-changes/${id}/accept`, 'POST');
export const rejectPlannerFixtureChange = (t, id)           => plannerApi(t, `/api/planner/fixture-changes/${id}/reject`, 'POST');
export const syncPlannerFixtures       = (t)                => plannerApi(t, '/api/planner/fixtures/sync', 'POST');
export const createPlannerFixture      = (t, data)          => plannerApi(t, '/api/planner/fixtures', 'POST', data);
export const updatePlannerFixture      = (t, id, data)      => plannerApi(t, `/api/planner/fixtures/${id}`, 'PUT', data);
export const deletePlannerFixture      = (t, id)            => plannerApi(t, `/api/planner/fixtures/${id}`, 'DELETE');
export const setPlannerDay             = (t, date, data)    => plannerApi(t, `/api/planner/days/${date}`, 'PUT', data);
export const setPlannerDays            = (t, changes)       => plannerApi(t, '/api/planner/days', 'PUT', { changes });
export const fillPlannerDays           = (t, data)          => plannerApi(t, '/api/planner/days/fill', 'POST', data);
export const getPlannerWeek            = (t, y, w)          => plannerApi(t, `/api/planner/week/${y}/${w}`);
export const publishPlannerWeek        = (t, y, w)          => plannerApi(t, `/api/planner/week/${y}/${w}/publish`, 'POST');
export const createPlannerBlock        = (t, data)          => plannerApi(t, '/api/planner/blocks', 'POST', data);
export const updatePlannerBlock        = (t, id, data)      => plannerApi(t, `/api/planner/blocks/${id}`, 'PUT', data);
export const deletePlannerBlock        = (t, id)            => plannerApi(t, `/api/planner/blocks/${id}`, 'DELETE');
export const duplicatePlannerBlock     = (t, id, dates)     => plannerApi(t, `/api/planner/blocks/${id}/duplicate`, 'POST', { dates });
export const copyPlannerDay            = (t, date, data)    => plannerApi(t, `/api/planner/days/${date}/copy-from`, 'POST', data);
export const getPlannerCategories      = (t)                => plannerApi(t, '/api/planner/categories');
export const getPlannerMe              = (t, from, to)      => plannerApi(t, `/api/planner/me?from=${from}&to=${to}`);
export const getPlannerTemplates       = (t)                => plannerApi(t, '/api/planner/templates');
export const getPlannerFillPreview     = (t, y, w, scenario) => plannerApi(t, `/api/planner/week/${y}/${w}/fill-preview${scenario ? `?scenario=${encodeURIComponent(scenario)}` : ''}`);
export const fillPlannerWeek           = (t, y, w, data)    => plannerApi(t, `/api/planner/week/${y}/${w}/fill`, 'POST', data);
export const getPlannerTemplateDiff    = (t, y, w, scenario, points) => plannerApi(t, `/api/planner/week/${y}/${w}/template-diff?scenario=${encodeURIComponent(scenario)}&points=${points}`);
export const savePlannerTemplate       = (t, y, w, data)    => plannerApi(t, `/api/planner/week/${y}/${w}/save-template`, 'POST', data);
export const getPlannerFeedToken       = (t)                => plannerApi(t, '/api/planner/feed-token');
export const createPlannerFeedToken    = (t)                => plannerApi(t, '/api/planner/feed-token', 'POST');
export const resetPlannerFeedToken     = (t)                => plannerApi(t, '/api/planner/feed-token/reset', 'POST');
export const getPlannerDayGroups       = (t, date)          => plannerApi(t, `/api/planner/days/${date}/groups`);
export const setPlannerDayGroups       = (t, date, data)    => plannerApi(t, `/api/planner/days/${date}/groups`, 'PUT', data);
export const setPlannerSlots           = (t, id, windows)   => plannerApi(t, `/api/planner/blocks/${id}/slots`, 'PUT', { windows });
export const autofillPlannerSlots      = (t, id)            => plannerApi(t, `/api/planner/blocks/${id}/slots/autofill`, 'POST');
export const createPlannerCategory     = (t, data)          => plannerApi(t, '/api/planner/categories', 'POST', data);
export const updatePlannerCategory     = (t, key, data)     => plannerApi(t, `/api/planner/categories/${key}`, 'PUT', data);
export const deletePlannerCategory     = (t, key, moveTo)   => plannerApi(t, `/api/planner/categories/${key}${moveTo ? `?move_to=${encodeURIComponent(moveTo)}` : ''}`, 'DELETE');

// ── Player name aliases ──────────────────────────────────────────────────────
export const getPlayerAliases    = ()     => api('/api/player-aliases');
export const createPlayerAlias   = (data) => api('/api/player-aliases', 'POST', data);
export const deletePlayerAlias   = (id)   => api(`/api/player-aliases/${id}`, 'DELETE');

// ── Team Overview ──────────────────────────────────────────────────────────────
export const getTeamOverview = () => api('/api/team-overview');
export const getNextMatch    = () => api('/api/next-match');
export const updateNextMatch = (data) => api('/api/next-match', 'PUT', data);

// ── Availability ──────────────────────────────────────────────────────────────
export const getAvailability = (dateFrom, dateTo, mode) =>
  api(`/api/availability?date_from=${dateFrom}&date_to=${dateTo}&mode=${mode || 'total'}`);
export const getAvailabilityOverrides   = (playerId) => api(`/api/availability-overrides${playerId ? `?player_id=${playerId}` : ''}`);
export const createAvailabilityOverride = (data)     => api('/api/availability-overrides', 'POST', data);
export const deleteAvailabilityOverride = (id)       => api(`/api/availability-overrides/${id}`, 'DELETE');
export const getLegacyStatusCodes       = ()         => api('/api/legacy-status-codes');

// ── Push notifications ────────────────────────────────────────────────────────
export const getVapidPublicKey       = ()              => api('/api/push/vapid-public-key');
export const sendRPEReminder         = ()              => api('/api/push/rpe-reminder', 'POST');
export const sendWellnessReminder    = ()              => api('/api/push/wellness-reminder', 'POST');
export const getWellnessReminderSettings    = ()     => api('/api/wellness-reminder-settings');
export const updateWellnessReminderSettings = (data) => api('/api/wellness-reminder-settings', 'PUT', data);
export const subscribeToPush         = (data)           => api('/api/push/subscribe', 'POST', data);
export const unsubscribeFromPush     = (endpoint)        => api('/api/push/unsubscribe', 'DELETE', { endpoint });
export const getNotificationPreferences    = (userId)    => api(`/api/notification-preferences/${userId}`);
export const updateNotificationPreferences = (userId, data) => api(`/api/notification-preferences/${userId}`, 'PUT', data);

// ── Medical options ────────────────────────────────────────────────────────────
export const getMedicalOptions  = (category) => api(`/api/medical-options${category ? `?category=${category}` : ''}`);
export const createMedicalOption = (data)    => api('/api/medical-options', 'POST', data);
export const updateMedicalOption = (id, data) => api(`/api/medical-options/${id}`, 'PUT', data);

// ── Injuries ──────────────────────────────────────────────────────────────────
export const getInjuries       = (playerId)   => api(`/api/injuries${playerId ? `?player_id=${playerId}` : ''}`);
export const createInjury      = (data)        => api('/api/injuries', 'POST', data);
export const updateInjuryStage = (id, data)    => api(`/api/injuries/${id}/stage`, 'PUT', data);
export const updateInjury      = (id, data)    => api(`/api/injuries/${id}`, 'PUT', data);
export const deleteInjury      = (id)          => api(`/api/injuries/${id}`, 'DELETE');
export const getInjuryHistory  = (id)          => api(`/api/injuries/${id}/history`);
export const addInjuryNote     = (id, data)    => api(`/api/injuries/${id}/notes`, 'POST', data);
export const getInjuryNotes    = (id)          => api(`/api/injuries/${id}/notes`);
export const updateInjuryNote  = (id, data)    => api(`/api/injury-notes/${id}`, 'PUT', data);
export const deleteInjuryNote  = (id)          => api(`/api/injury-notes/${id}`, 'DELETE');

// ── Illnesses ─────────────────────────────────────────────────────────────────
export const getIllnesses      = (playerId)   => api(`/api/illnesses${playerId ? `?player_id=${playerId}` : ''}`);
export const createIllness     = (data)        => api('/api/illnesses', 'POST', data);
export const updateIllness     = (id, data)    => api(`/api/illnesses/${id}`, 'PUT', data);
export const addIllnessNote    = (id, data)    => api(`/api/illnesses/${id}/notes`, 'POST', data);
export const getIllnessNotes   = (id)          => api(`/api/illnesses/${id}/notes`);
export const updateIllnessNote = (id, data)    => api(`/api/illness-notes/${id}`, 'PUT', data);
export const deleteIllnessNote = (id)          => api(`/api/illness-notes/${id}`, 'DELETE');

// ── Appointments ──────────────────────────────────────────────────────────────
export const getAppointments   = (playerId)   => api(`/api/appointments${playerId ? `?player_id=${playerId}` : ''}`);
export const createAppointment = (data)        => api('/api/appointments', 'POST', data);
export const updateAppointment = (id, data)    => api(`/api/appointments/${id}`, 'PUT', data);
// Player app — the caller's own appointment popups (new + day-of reminder), resolved from the session.
export const getAppointmentPopups    = (sessionToken)     => apiAuth(sessionToken, '/api/players/me/appointment-popups');
export const dismissAppointmentPopup = (sessionToken, id) => apiAuth(sessionToken, `/api/players/me/appointment-popups/${id}/dismiss`, 'POST');

// ── Physical testing ──────────────────────────────────────────────────────────
export const getTests    = (type, playerId) => api(`/api/tests/${type}${playerId ? `?player_id=${playerId}` : ''}`);
export const createTest  = (type, data)     => api(`/api/tests/${type}`, 'POST', data);
export const getISRTPercentiles = () => api('/api/tests/isrt-percentiles');
export const getSprintPercentiles = () => api('/api/tests/sprint-percentiles');
export const getSquadProfiling = () => api('/api/tests/squad-profiling');
export const deleteTestSession = (playerId, testDate) => api(`/api/tests/session?player_id=${playerId}&test_date=${testDate}`, 'DELETE');
export const deleteTest        = (type, playerId, testDate) => api(`/api/tests/${type}?player_id=${playerId}&test_date=${testDate}`, 'DELETE');
export const syncPassingDrillTests          = () => api('/api/tests/passing-drill/sync', 'POST');
export const syncIsrtMaxHR                  = () => api('/api/tests/isrt/sync-hr', 'POST');
export const getPassingDrillCalibration     = () => api('/api/passing-drill-calibration');
export const updatePassingDrillCalibration  = (data) => api('/api/passing-drill-calibration', 'PUT', data);
export const getTestInputAccess     = () => api('/api/test-input-access');
export const grantTestInputAccess   = (data) => api('/api/test-input-access', 'POST', data);
export const revokeTestInputAccess  = (accountId) => api(`/api/test-input-access/${accountId}`, 'DELETE');

export const getTestNotes = (playerId) => api(`/api/test-notes${playerId ? `?player_id=${playerId}` : ''}`);
export const saveTestNote = (data)     => api('/api/test-notes', 'POST', data);

// ── Medical Log ───────────────────────────────────────────────────────────────
export const getMedicalLog    = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));
  return api(`/api/medical-log${q.toString() ? `?${q}` : ''}`);
};
export const createMedicalLog = (data) => api('/api/medical-log', 'POST', data);
export const updateMedicalLog = (id, data) => api(`/api/medical-log/${id}`, 'PUT', data);
export const deleteMedicalLog = (id)   =>api(`/api/medical-log/${id}`, 'DELETE');

// ── Forms ─────────────────────────────────────────────────────────────────────
export const getForms         = ()             => api('/api/forms');
export const createForm       = (data)         => api('/api/forms', 'POST', data);
export const updateForm       = (id, data)     => api(`/api/forms/${id}`, 'PUT', data);
export const deleteForm       = (id)           => api(`/api/forms/${id}`, 'DELETE');
export const sendForm         = (id, data)     => api(`/api/forms/${id}/send`, 'POST', data);
export const closeForm        = (id)           => api(`/api/forms/${id}/close`, 'POST');
export const getFormResponses = (id)           => api(`/api/forms/${id}/responses`);
export const getPendingForms  = (playerId)     => api(`/api/players/me/forms/pending?player_id=${playerId}`);
export const getFormToFill    = (id, playerId) => api(`/api/forms/${id}/fill?player_id=${playerId}`);
export const submitFormResponse = (id, data)   => api(`/api/forms/${id}/respond`, 'POST', data);

// ── Week schedule ─────────────────────────────────────────────────────────────
export const getScheduleWeeks     = ()               => api('/api/schedule/weeks');
export const getScheduleWeek      = (weekStartDate)  => api(`/api/schedule/weeks/${weekStartDate}`);
export const getScheduleDay       = (date)           => api(`/api/schedule/day/${date}`);
export const createScheduleWeek   = (data)           => api('/api/schedule/weeks', 'POST', data);
export const patchScheduleDay     = (dayId, data)    => api(`/api/schedule/days/${dayId}`, 'PATCH', data);
export const finalizeScheduleWeek = (id)             => api(`/api/schedule/weeks/${id}/finalize`, 'POST');
export const resyncScheduleWeek   = (id)             => api(`/api/schedule/weeks/${id}/resync`, 'POST');
export const duplicateScheduleWeek = (id, newWeekStartDate) =>
  api(`/api/schedule/weeks/${id}/duplicate`, 'POST', { new_week_start_date: newWeekStartDate });

// ── Period League (ssg_*) ────────────────────────────────────────────────────
// No auth middleware anywhere in this app (see server.staging.js's Forms section comment) —
// requesterRole is trusted straight from the caller, same convention as Week Schedule's
// PATCH /api/schedule/days/:dayId. Query string for GETs and for the points array PUT (its body
// IS the array, so requesterRole can't live inside it); body field everywhere else.
export const getSsgPeriods   = (season) => api(`/api/ssg/periods${season ? `?season=${encodeURIComponent(season)}` : ''}`);
export const createSsgPeriod = (data, requesterRole) => api('/api/ssg/periods', 'POST', { ...data, requesterRole });
export const updateSsgPeriod = (id, data, requesterRole) => api(`/api/ssg/periods/${id}`, 'PUT', { ...data, requesterRole });
export const closeSsgPeriod  = (id, requesterRole, force) =>
  api(`/api/ssg/periods/${id}/close?requesterRole=${requesterRole}${force ? '&force=true' : ''}`, 'POST');

export const getSsgSessions = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
  return api(`/api/ssg/sessions${q.toString() ? `?${q}` : ''}`);
};
export const getSsgSession    = (id)   => api(`/api/ssg/sessions/${id}`);
export const createSsgSession = (data, requesterRole) => api('/api/ssg/sessions', 'POST', { ...data, requesterRole });
export const updateSsgSession = (id, data, requesterRole) => api(`/api/ssg/sessions/${id}`, 'PUT', { ...data, requesterRole });
export const deleteSsgSession = (id, requesterRole) => api(`/api/ssg/sessions/${id}?requesterRole=${requesterRole}`, 'DELETE');
export const getSsgSessionPoints = (id) => api(`/api/ssg/sessions/${id}/points`);
export const putSsgSessionPoints = (id, rows, requesterRole) =>
  api(`/api/ssg/sessions/${id}/points?requesterRole=${requesterRole}`, 'PUT', rows);
export const publishSsgSession = (id, requesterRole) => api(`/api/ssg/sessions/${id}/publish?requesterRole=${requesterRole}`, 'POST');

export const getSsgLeaderboard = (periodId, scope = 'period') => api(`/api/ssg/leaderboard?period_id=${periodId}&scope=${scope}`);
export const getSsgProgression = (periodId) => api(`/api/ssg/leaderboard/progression?period_id=${periodId}`);
export const getSsgPlayerSummary = (playerId, periodId) => api(`/api/ssg/player/${playerId}/summary?period_id=${periodId}`);
export const getSsgHeadToHead = (a, b, periodId) => api(`/api/ssg/head-to-head?a=${a}&b=${b}${periodId ? `&period_id=${periodId}` : ''}`);
export const getSsgBadges     = (periodId) => api(`/api/ssg/badges?period_id=${periodId}`);
export const getSsgHallOfFame = (season) => api(`/api/ssg/hall-of-fame${season ? `?season=${encodeURIComponent(season)}` : ''}`);

// Dressing-room TV — /api/ssg/tv is token-only (no requesterRole, no session), the kiosk route
// never logs in. Token admin (list/create/revoke) is the usual requesterRole-gated pattern.
export const getSsgTv = (token) => api(`/api/ssg/tv?token=${encodeURIComponent(token)}`);
export const getSsgTvTokens   = (requesterRole) => api(`/api/ssg/tv-tokens?requesterRole=${requesterRole}`);
export const createSsgTvToken = (data, requesterRole) => api('/api/ssg/tv-tokens', 'POST', { ...data, requesterRole });
export const updateSsgTvToken = (token, data, requesterRole) => api(`/api/ssg/tv-tokens/${encodeURIComponent(token)}`, 'PUT', { ...data, requesterRole });

// ── Documents (PDF upload, staging only) ────────────────────────────────────────
// uploadDocuments bypasses the shared api() helper for two reasons: it needs the same
// specific-error-message behavior as uploadTechnicalStats above, and — the harder requirement —
// a FormData body must NOT get a manually-set 'Content-Type' header, since the browser has to
// generate the multipart boundary itself; api()'s hardcoded 'application/json' header would
// break the upload outright.
export const uploadDocuments = async (files, { category, requesterRole, uploadedBy } = {}) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  formData.append('category', category || 'Other');
  formData.append('requesterRole', requesterRole);
  if (uploadedBy) formData.append('uploadedBy', uploadedBy);
  const res = await fetch(`${BASE_URL}/api/documents/upload`, { method: 'POST', body: formData });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `API error: ${res.status}`);
  return body;
};
export const getAllDocuments       = (requesterRole) => api(`/api/documents?requesterRole=${requesterRole}`);
export const getPlayerDocuments    = (playerId) => api(`/api/documents/player/${playerId}`);
export const getUnmatchedDocuments = (requesterRole) => api(`/api/documents/unmatched?requesterRole=${requesterRole}`);
export const assignDocument = (id, playerId, requesterRole, assignedBy) =>
  api(`/api/documents/${id}/assign`, 'POST', { player_id: playerId, requesterRole, assignedBy });
export const deleteDocument = (id, requesterRole) => api(`/api/documents/${id}?requesterRole=${requesterRole}`, 'DELETE');
export const setDocumentNote = (id, note, requesterRole) => api(`/api/documents/${id}/note`, 'PUT', { note, requesterRole });
export const documentViewUrl     = (id) => `${BASE_URL}/api/documents/${id}/view`;
export const documentDownloadUrl = (id) => `${BASE_URL}/api/documents/${id}/download`;
export const DOCUMENT_CATEGORIES = ['Technical/tactical', 'Medical', 'Performance', 'Nutrition', 'Administrative', 'Other'];
